import CommonBadge from "@/components/Common/Badge";
import DataTableColumnHeader from "@/components/Common/DataTable/DataTableColumnHeader";
import { calculateDDay, cn } from "@/libs/utils";
import { ProjectGroup, TaskStatusMap } from "@/types/project";
import { ColumnDef } from "@tanstack/react-table";
import { fromUnixTime } from "date-fns";
import { User } from "@/types/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import TaskStatus from "./components/TaskStatus";
import { PROJECT_TYPE_TITLE, ProjectType } from "@/consts/Project";
import { Plus } from "lucide-react";
import { useUpdateProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useState } from "react";
import { ReactComponent as StatusCancelIcon } from "@/assets/images/status-cancel.svg";

const PROJECT_STATUS_OPTIONS = [
  {
    value: "1. 산출내역 요청",
    label: "1. 산출내역 요청",
    color: "bg-dark-red text-white",
  },
  {
    value: "2. 산출내역 작성",
    label: "2. 산출내역 작성",
    color: "bg-[#713637] text-white",
  },
  {
    value: "3. 산출내역 완료",
    label: "3. 산출내역 완료",
    color: "bg-[#3B1213] text-white",
  },
  {
    value: "4. 시나리오 요청",
    label: "4. 시나리오 요청",
    color: "bg-blue-B text-white",
  },
  {
    value: "5. 시나리오 분석",
    label: "5. 시나리오 분석",
    color: "bg-[#0186C7] text-white",
  },
  {
    value: "6. 시나리오 완료",
    label: "6. 시나리오 완료",
    color: "bg-[#00547E] text-white",
  },
  {
    value: "7. 투찰승인 요청",
    label: "7. 투찰승인 요청",
    color: "bg-green text-white",
  },
  {
    value: "8. 투찰승인 완료",
    label: "8. 투찰승인 완료",
    color: "bg-[#4E8775] text-white",
  },
  {
    value: "9. 투찰 완료",
    label: "9. 투찰 완료",
    color: "bg-[#376155] text-white",
  },
  {
    value: "10. 투찰 포기",
    label: "10. 투찰 포기",
    color: "bg-gray-500 text-white",
  },
];

const StatusCell = ({
  groupId,
  initialValue,
}: {
  groupId: string;
  initialValue?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempClearedStatus, setTempClearedStatus] = useState(false);
  const updateProjectGroups = useUpdateProjectGroups(groupId);

  const handleSelect = (newValue: string) => {
    updateProjectGroups.mutate({
      status: newValue,
    });
    setTempClearedStatus(false);
    setIsOpen(false);
  };

  const handleClearStatus = () => {
    setTempClearedStatus(true);
    // API 호출하지 않고 로컬 상태만 변경
  };

  const currentStatus = PROJECT_STATUS_OPTIONS.find(
    (option) => option.value === initialValue
  );

  const isCurrentlyCleared = tempClearedStatus;

  return (
    <div className="relative w-full" onClick={(e) => e.stopPropagation()}>
      {/* 테이블 뱃지 표시 */}
      {currentStatus && !isCurrentlyCleared ? (
        <div
          className={cn(
            "flex items-center gap-[10px] px-1 py-[2px] text-xs font-medium cursor-pointer hover:opacity-80 rounded",
            currentStatus.color
          )}
          onClick={() => setIsOpen(!isOpen)}
          style={{ width: "fit-content" }}
        >
          {currentStatus.label}
        </div>
      ) : (
        <div
          className="flex items-center gap-[10px] px-1 py-[2px] text-xs font-medium cursor-pointer bg-gray-200 text-gray-600 hover:bg-gray-300 rounded"
          onClick={() => setIsOpen(!isOpen)}
          style={{ width: "fit-content" }}
        >
          상태 선택
        </div>
      )}

      {/* 드롭다운 */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white shadow-lg z-50"
          style={{ width: "fit-content", minWidth: "100px" }}
        >
          <div
            className="flex min-w-[100px] px-3 py-2 items-center gap-1 bg-white border border-[#E1E3E5]"
            style={{ borderRadius: "4px 4px 0px 0px" }}
          >
            {currentStatus && !isCurrentlyCleared && (
              <div
                className={cn(
                  "flex items-center gap-[10px] px-1 py-[2px] text-xs font-medium rounded",
                  currentStatus.color
                )}
                style={{ width: "fit-content" }}
              >
                {currentStatus.label}
                <StatusCancelIcon
                  className="w-3 h-3 cursor-pointer hover:opacity-70"
                  onClick={handleClearStatus}
                />
              </div>
            )}
          </div>

          {/* 다른 옵션들 영역 */}
          <div
            className="flex px-3 py-2 flex-col items-start gap-2 bg-white border-r border-b border-l border-[#E1E3E5]"
            style={{
              borderRadius: "0px 0px 4px 4px",
            }}
          >
            {PROJECT_STATUS_OPTIONS.map((option) => {
              // 현재 선택된 상태가 아니거나, 임시로 해제된 경우에만 표시
              if (option.value === initialValue && !isCurrentlyCleared) {
                return null;
              }

              return (
                <div
                  key={option.value}
                  className={cn(
                    "flex items-center gap-[10px] px-1 py-[2px] text-xs font-medium cursor-pointer hover:opacity-80 rounded",
                    option.color
                  )}
                  onClick={() => handleSelect(option.value)}
                  style={{ width: "fit-content" }}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 배경 클릭시 닫기 */}
      {isOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
      )}
    </div>
  );
};

export const columns: ColumnDef<ProjectGroup & { disabled: boolean }>[] = [
  {
    accessorKey: "project_group_title",
    header: ({ column }) => (
      <DataTableColumnHeader title="프로젝트명" column={column} />
    ),
    cell: ({ row }) => {
      const projectTitle = row.getValue("project_group_title");

      return (
        <span className="text-gray-A text-sm font-normal">
          {projectTitle as string}
        </span>
      );
    },
    enableSorting: false,
    size: 810,
  },
  {
    accessorKey: "users",
    header: ({ column }) => (
      <DataTableColumnHeader title="참여자" column={column} />
    ),
    cell: ({ row }) => {
      const users = row.getValue("users") as User[];
      return (
        <div className="flex items-center gap-4">
          {users.slice(0, 2).map((user, index) => (
            <div
              className="flex items-center gap-2"
              key={`users-${user.user_id}-${index}`}
            >
              <Avatar
                key={`users-${user.user_id}-${index}`}
                className={`size-4 ${
                  index === 0
                    ? "z-30"
                    : index === 1
                      ? "z-20 -ml-1"
                      : "z-10 -ml-1"
                }`}
              >
                <AvatarImage src={user.img_url} />
                <AvatarFallback>{user.user_name}</AvatarFallback>
              </Avatar>
              <span className="text-gray-A text-xs font-normal">
                {user.user_name}
              </span>
            </div>
          ))}
          {users.length > 2 && (
            <div className="flex items-center text-gray-B">
              <Plus className="size-3.5" />
              <span className="text-sm font-semibold">{users.length - 2}</span>
            </div>
          )}
        </div>
      );
    },
    enableSorting: false,
    size: 232,
  },
  {
    accessorKey: "project_stage",
    header: ({ column }) => (
      <DataTableColumnHeader title="입찰단계" column={column} />
    ),
    cell: ({ row }) => {
      const projectStage: ProjectType = row.getValue("project_stage");
      return (
        <CommonBadge
          content={PROJECT_TYPE_TITLE[projectStage]}
          className={cn(
            "ml-0 min-w-fit w-fit px-1 py-[2px] text-center text-[10px] font-bold rounded-md",
            {
              "bg-mediumGreen": projectStage === ProjectType.PROJ,
              "bg-dark-blue": projectStage === ProjectType.PRCR,
              "bg-mandalay": projectStage === ProjectType.PLAN,
            }
          )}
        />
      );
    },
    enableSorting: false,
    size: 133,
  },
  {
    accessorKey: "due_date",
    header: ({ column }) => (
      <DataTableColumnHeader title="공고마감일" column={column} />
    ),
    cell: ({ row }) => {
      const dueDate = row.getValue("due_date") as number;
      const dDay = calculateDDay(dueDate);

      const getDDayText = () => {
        if (dDay === 0) return "D-Day";
        if (dDay > 0) return `D-${dDay}`;
        return `D+${Math.abs(dDay)}`;
      };

      return (
        <span
          className={cn("text-[13px] font-normal", {
            "text-red font-bold": dDay <= 3 && dDay >= 0, // D-3일 이하일 때 빨간색 강조
            "text-gray-A opacity-60": dDay > 3 || dDay < 0, // 그 외에는 기본 스타일
          })}
        >
          {getDDayText()}
        </span>
      );
    },
    enableSorting: false,
    size: 115,
  },
  {
    accessorKey: "task_status_map",
    header: ({ column }) => (
      <DataTableColumnHeader title="작업 진행률" column={column} />
    ),
    cell: ({ row }) => (
      <TaskStatus
        taskStatusMap={row.getValue("task_status_map")}
        isParticipating={row.original.is_participating}
      />
    ),
    enableSorting: false,
    size: 180,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader title="프로젝트 상태" column={column} />
    ),
    cell: ({ row }) => (
      <StatusCell
        groupId={row.original.project_group_id}
        initialValue={row.getValue("status")}
      />
    ),
    enableSorting: false,
    size: 200,
  },
];
